import { Controller } from '@hotwired/stimulus'
import TomSelect from 'tom-select/src/tom-select.popular'
import { TomInput, TomOption } from 'tom-select/src/types/index'
import checkboxOptions from 'tom-select/src/plugins/checkbox_options/plugin'
import removeButton from 'tom-select/src/plugins/remove_button/plugin'
import { escape_html } from 'tom-select/src/utils'
import { decorateTomSelectWithDataCy } from '../../modules/tom_select'
import { isInViewport, observeWhenVisible } from '../../modules/utils'

TomSelect.define('checkbox_options', checkboxOptions)
TomSelect.define('remove_button', removeButton)

const searchIcon =
  '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true" class="search-icon">' +
  '<path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"></path>' +
  '</svg>'

interface MultiselectTomOption extends TomOption {
  text: string
  selectedTitle: string
  itemType: string
  catalogue?: string
  badge?: string
}

interface MultiselectChangeDetail {
  selectId: string
  value: string
  action: string
}

export default class extends Controller {
  static targets = ['select', 'create']
  static values = {
    create: Boolean,
    icon: Boolean,
    hidePlaceholder: Boolean,
    badges: Boolean
  }

  declare createValue: boolean
  declare iconValue: boolean
  declare hidePlaceholderValue: boolean
  declare badgesValue: boolean

  declare readonly hasSelectTarget: boolean
  declare readonly selectTarget: HTMLElement

  observer: IntersectionObserver
  changed: boolean

  private _plugin: TomSelect

  get plugin (): TomSelect {
    if (this._plugin == null) {
      this.initializePlugin()
    }
    return this._plugin
  }

  connect (): void {
    if (isInViewport(this.element)) {
      // Load visible element immediately to avoid UI shift
      this.initializePlugin()
    } else {
      // Defer initialization of offscreen elements
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => this.observeWhenVisible(), { timeout: 100 })
      } else {
        setTimeout(() => this.observeWhenVisible(), 100)
      }
    }
  }

  disconnect (): void {
    if (!document.body.contains(this.element)) {
      if (this._plugin != null) {
        this.plugin.destroy()
      }
      if (this.observer != null) {
        this.observer.disconnect()
      }
    }
  }

  private initializePlugin (): void {
    if (this._plugin != null) return

    const selectElement = this.hasSelectTarget ? this.selectTarget : this.element

    this._plugin = new TomSelect(selectElement as TomInput, {
      maxOptions: 1000,
      plugins: ['checkbox_options', 'remove_button'],
      hidePlaceholder: this.hidePlaceholderValue,
      create: this.createValue,
      render: this.badgesValue
        ? {
            option: (data: MultiselectTomOption, escape: typeof escape_html) => this.renderBadgedOption(data, escape),
            item: (data: MultiselectTomOption, escape: typeof escape_html) => this.renderBadgedItem(data, escape)
          }
        : { item: this.renderItem },
      onItemRemove: (value: string) => {
        this.dispatchItemChangeEvent(value, 'remove')
        this.notifyRiskTemplateFilter(value, 'remove')
      },
      onOptionAdd: (value: string) => {
        this.dispatchItemChangeEvent(value, 'add-option')
      },
      onItemAdd: (value: string, item: HTMLElement) => {
        this._plugin.setTextboxValue('')
        requestAnimationFrame(() => this._plugin.refreshOptions())
        this.dispatchItemChangeEvent(value, 'add', item)
        this.notifyRiskTemplateFilter(value, 'add')
      },
      onChange: () => {
        this.changed = true
        if (!this._plugin.isOpen) this.dispatchChangedEvent()
      },
      onDropdownClose: () => this.dispatchChangedEvent()
    })

    this._plugin.on('focus', () => {
      requestAnimationFrame(() => this.selectDropdownPosition())
    })
    this._plugin.on('type', () => {
      requestAnimationFrame(() => this.selectDropdownPosition())
    })

    if (this.iconValue) {
      requestAnimationFrame(() => {
        if (((this._plugin.control) != null) && (this._plugin.control.querySelector('.search-icon') == null)) {
          this._plugin.control.insertAdjacentHTML('beforeend', searchIcon)
        }
      })
    }

    decorateTomSelectWithDataCy(selectElement as HTMLElement, this.plugin)
    const element = this.element as any
    element.tomSelect = this._plugin

    this.hideLoader()
  }

  dispatchItemChangeEvent (value: string, action: string, item?: HTMLElement): void {
    this.dispatch(action, { bubbles: true, detail: { value, item } })
  }

  dispatchChangedEvent (): void {
    if (!this.changed) return

    this.dispatch('changed')
    this.changed = false
  }

  notifyRiskTemplateFilter (value: string, action: string): void {
    const selectId = this.element.id
    const detail: MultiselectChangeDetail = { selectId, value, action }

    const event = new CustomEvent('multiselect:change', {
      bubbles: true,
      detail
    })

    this.element.dispatchEvent(event)
  }

  touch (): void {
    this.wrapperClassList().add('changed')
  }

  reset (): void {
    this.plugin.clear(true)
    this.wrapperClassList().remove('changed')
  }

  resetOptions ({ detail: { values } }: { detail: { values: any[] } }): void {
    const options: TomOption[] = values.map(value => ({ value: value.id, text: value.name }))

    this.plugin.clear(true)
    this.plugin.clearOptions()
    this.plugin.addOptions(options)
  }

  filterOptions ({ detail: { itemType } }: { detail: { itemType: string } }): void {
    this.plugin.clear(true)
    this.plugin.sync(true)

    Object.keys(this.plugin.options ?? {}).forEach((key) => {
      if (this.plugin.options[key]?.itemType !== itemType) {
        this.plugin.removeOption(key)
      }
    })
  }

  wrapperClassList (): DOMTokenList {
    if (this._plugin == null) {
      throw new Error('TomSelect plugin is not initialized yet.')
    }
    return this.plugin.wrapper.classList
  }

  selectDropdownPosition (): void {
    requestAnimationFrame(() => {
      const plugin = this.plugin
      if (plugin == null || plugin.dropdown == null || plugin.control_input == null) return

      const inputRect = plugin.control_input.getBoundingClientRect()
      const spaceBelow = window.innerHeight - inputRect.bottom

      plugin.dropdown.style.top = (spaceBelow > 260) ? '' : `-${plugin.dropdown.clientHeight + 10}px`
    })
  }

  renderItem (data: MultiselectTomOption, escape: typeof escape_html): string {
    const text = data.selectedTitle ?? data.text
    return `<div><p>${escape(text)}</p></div>`
  }

  renderBadgedOption (data: MultiselectTomOption, escape: typeof escape_html): string {
    const optionClass = data.catalogue === 'linked' ? 'option-badged option-on-application' : 'option-badged'
    return `<div class="${optionClass}"><p>${escape(data.text)}</p>${this.renderBadge(data, escape)}</div>`
  }

  renderBadgedItem (data: MultiselectTomOption, escape: typeof escape_html): string {
    if (data.catalogue !== 'addition') return this.renderItem(data, escape)

    return `<div class="item-badged"><p>${escape(data.text)}</p>${this.renderBadge(data, escape)}</div>`
  }

  private renderBadge (data: MultiselectTomOption, escape: typeof escape_html): string {
    if (data.badge == null) return ''

    const badgeClass = data.catalogue === 'addition' ? 'lt-badge lt-badge-sm lt-badge-outline' : 'lt-badge lt-badge-sm'
    return `<span class="${badgeClass}">${escape(data.badge)}</span>`
  }

  private hideLoader (): void {
    const wrapper = this.element.parentElement
    const loader = wrapper?.querySelector('[data-loader]')

    if (loader != null) loader.remove()
  }

  private observeWhenVisible (): void {
    if (this._plugin != null) return
    this.observer = observeWhenVisible(this.element, () => {
      requestAnimationFrame(() => {
        this.initializePlugin()
      })
    })
  }
}
